/**
 * The layout slice handles the layout of the application.
 * That includes dialog, sidebar, toast, gallery, and search UI state.
 * Language, theme, date format, and time format preferences are stored in localStorage.
 */

import { createSlice } from "@reduxjs/toolkit"
import { Toast } from "types"

export const layoutSlice = createSlice({
    name: 'layout',
    initialState: {
        showDialog: false,
        dialogQueue: [] as string[],
        dialogData: {} as Record<string, any>,
        sidebarIsVisible: false,
        toasts: [] as Toast[],
        galleryFullscreen: false,
        searchDisplayDeleted: false,
    },
    reducers: {
        /**
         * Add toast message to the state.
         * Note: Toast removal should be handled by middleware or components with useEffect
         */
        addToastAction: (state, action) => {
            // If toast with same text already exists, do not add it again
            if (state.toasts.some(toast => toast.text === action.payload.text)) return
            // Add toast
            const toast: Toast = {
                text: action.payload.text,
                colorScheme: action.payload.colorScheme || 'gray',
            }
            state.toasts.push(toast)
        },
        removeFirstToastAction: (state) => {
            if (state.toasts.length > 0) {
                state.toasts.shift()
            }
        },
        showDialogAction: (state, action) => {
            state.showDialog = true
            const dialogId = action.payload.dialog_id
            const existingIndex = state.dialogQueue.indexOf(dialogId)
            if (existingIndex !== -1) {
                state.dialogQueue.splice(existingIndex, 1)
            }
            state.dialogQueue.push(dialogId)
            if (action.payload.data) {
                state.dialogData[dialogId] = action.payload.data
            }
        },
        hideDialogAction: (state) => {
            const dialogId = state.dialogQueue[state.dialogQueue.length - 1]
            if (dialogId && state.dialogData[dialogId]) {
                delete state.dialogData[dialogId]
            }
            state.dialogQueue.pop()
            state.showDialog = !!state.dialogQueue.length
        },
        showSidebarAction: (state) => {
            state.sidebarIsVisible = true
        },
        hideSidebarAction: (state) => {
            state.sidebarIsVisible = false
        },
        showGalleryFullscreenAction: (state) => {
            state.galleryFullscreen = true
        },
        hideGalleryFullscreenAction: (state) => {
            state.galleryFullscreen = false
        },
        setSearchDisplayDeletedAction: (state, action) => {
            state.searchDisplayDeleted = Boolean(action.payload)
        },
    },
})

export const { 
    showDialogAction, 
    hideDialogAction, 
    showSidebarAction, 
    hideSidebarAction, 
    addToastAction,
    removeFirstToastAction,
    showGalleryFullscreenAction,
    hideGalleryFullscreenAction,
    setSearchDisplayDeletedAction,
} = layoutSlice.actions

export default layoutSlice.reducer
